import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileText, Bot, Users, Shield, Upload, Search, 
  MessageSquare, Layers, Database, Sparkles, 
  Clock, TrendingUp, FolderOpen, Plus, Zap,
  ArrowUpRight, Brain, FileSearch
} from 'lucide-react';
import docsApiClient from '@/api/docsClient';
import { getCurrentUserWithPermissions, getRoleBadge } from '@/utils/rolePermissions';

export default function DocsDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalQueries: 0,
    activeUsers: 0,
    storageUsed: 0,
    storageLimit: 50,
    documentsThisWeek: 0,
    queriesThisWeek: 0,
    avgResponseTime: '1.2s'
  });
  const [loading, setLoading] = useState(true);
  const [recentDocuments, setRecentDocuments] = useState([]);

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  
  // Get user permissions
  const user = getCurrentUserWithPermissions();
  const permissions = user?.docPermissions || {};
  const roleBadge = getRoleBadge(user?.doc_role || user?.role, 'docs');

  useEffect(() => {
    fetchStats();
    fetchRecentDocuments();
  }, []);

  const fetchRecentDocuments = async () => {
    try {
      const response = await docsApiClient.get('/documents?limit=4');
      if (response.data) {
        const docs = response.data.slice(0, 4).map(doc => ({
          id: doc.id,
          name: doc.title || doc.filename || 'Untitled',
          type: (doc.filename || '').split('.').pop() || 'pdf',
          size: formatFileSize(doc.file_size || 0),
          uploadedAt: formatTimeAgo(doc.created_at || doc.uploaded_at)
        }));
        setRecentDocuments(docs);
      }
    } catch (error) {
      console.log('Could not fetch recent documents');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, storageRes] = await Promise.all([
        docsApiClient.get('/dashboard/stats'),
        docsApiClient.get('/storage/usage').catch(() => ({ data: null }))
      ]);
      
      if (statsRes.data) {
        // Map API response to state format
        const apiData = statsRes.data;
        setStats(prev => ({
          ...prev,
          totalDocuments: apiData.documents_uploaded || 0,
          totalQueries: apiData.questions_asked || 0,
          activeUsers: apiData.active_users || 0,
        }));
      }
      
      if (storageRes.data) {
        setStats(prev => ({
          ...prev,
          storageUsed: (storageRes.data.used_bytes || 0) / (1024 * 1024 * 1024),
          storageLimit: (storageRes.data.limit_bytes || 50 * 1024 * 1024 * 1024) / (1024 * 1024 * 1024),
        }));
      }
    } catch (error) {
      console.log('Using default stats:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Documents', 
      value: stats.totalDocuments.toLocaleString(), 
      change: `+${stats.documentsThisWeek} this week`,
      icon: FileText, 
      bgColor: 'bg-purple-500/10',
      textColor: 'text-purple-400',
      href: '/suite/docs/documents' 
    },
    { 
      title: 'AI Queries', 
      value: stats.totalQueries.toLocaleString(), 
      change: `+${stats.queriesThisWeek} this week`,
      icon: MessageSquare, 
      bgColor: 'bg-blue-500/10',
      textColor: 'text-blue-400',
      href: '/suite/docs/queries' 
    },
    { 
      title: 'Active Users', 
      value: stats.activeUsers, 
      change: 'Team members',
      icon: Users, 
      bgColor: 'bg-green-500/10',
      textColor: 'text-green-400',
      href: '/suite/docs/users' 
    },
    { 
      title: 'Avg Response', 
      value: stats.avgResponseTime, 
      change: 'AI processing time',
      icon: Zap, 
      bgColor: 'bg-amber-500/10',
      textColor: 'text-amber-400',
      href: '/suite/docs/reports' 
    },
  ];

  const quickActions = [
    { label: 'AI Chat', icon: Bot, onClick: () => navigate('/suite/docs/chat'), color: 'bg-purple-600 hover:bg-purple-700', primary: true, show: true },
    { label: 'Upload Document', icon: Upload, onClick: () => navigate('/suite/docs/documents'), color: 'bg-blue-600 hover:bg-blue-700', show: permissions.canUploadDocuments },
    { label: 'Compare Docs', icon: FileSearch, onClick: () => navigate('/suite/docs/compare'), color: 'bg-emerald-600 hover:bg-emerald-700', show: permissions.canManageAllDocs },
    { label: 'Batch Analysis', icon: Layers, onClick: () => navigate('/suite/docs/batch'), color: 'bg-orange-600 hover:bg-orange-700', show: permissions.canBatchAnalyze },
  ].filter(action => action.show);

  const getFileIcon = (type) => {
    switch(type) {
      case 'pdf': return <FileText className="w-5 h-5 text-red-400" />;
      case 'docx': return <FileText className="w-5 h-5 text-blue-400" />;
      case 'xlsx': return <FileText className="w-5 h-5 text-green-400" />;
      default: return <FileText className="w-5 h-5 text-slate-400" />;
    }
  };

  const storagePercent = (stats.storageUsed / stats.storageLimit) * 100;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold text-white">Document Intelligence</h1>
            <Badge className={`${roleBadge.color} text-white text-xs`}>
              <Shield className="w-3 h-3 mr-1" />
              {roleBadge.label}
            </Badge>
          </div>
          <p className="text-slate-400 mt-1">AI-powered document management and analysis</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/suite/docs/chat')} className="bg-purple-600 hover:bg-purple-700">
            <Bot className="w-4 h-4 mr-2" />
            AI Chat
          </Button>
          {permissions.canUploadDocuments && (
            <Button onClick={() => navigate('/suite/docs/documents')} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card 
            key={stat.title} 
            className="bg-slate-900 border-slate-800 cursor-pointer hover:border-slate-600 transition-all hover:scale-[1.02]"
            onClick={() => navigate(stat.href)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 uppercase tracking-wider">{stat.title}</p>
                  <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3 text-green-400" />
                    {stat.change}
                  </p>
                </div>
                <div className={`w-14 h-14 rounded-2xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-7 h-7 ${stat.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Assistant Promo */}
      <Card className="bg-gradient-to-r from-purple-900/40 to-violet-900/40 border-purple-700/50 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
        <CardContent className="p-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shrink-0">
              <Brain className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-bold text-white mb-2">AI Document Assistant</h3>
              <p className="text-slate-300 max-w-xl">
                Ask questions about your documents and get instant, intelligent answers. 
                Our GPT-4 powered assistant understands context and delivers precise insights from your document library.
              </p>
            </div>
            <Button 
              size="lg"
              className="bg-purple-600 hover:bg-purple-700 px-8"
              onClick={() => navigate('/suite/docs/chat')}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Chatting
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Storage Usage */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-400" />
              Storage Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Used</span>
                  <span className="text-white font-semibold">{stats.storageUsed} GB / {stats.storageLimit} GB</span>
                </div>
                <Progress value={storagePercent} className="h-3 bg-slate-700" />
              </div>
              <p className="text-sm text-slate-500">
                {(stats.storageLimit - stats.storageUsed).toFixed(1)} GB remaining
              </p>
              <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800">
                Manage Storage
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                onClick={action.onClick}
                className={`w-full justify-start ${action.color}`}
              >
                <action.icon className="w-4 h-4 mr-3" />
                {action.label}
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Recent Documents */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Recent Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                    {getFileIcon(doc.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{doc.name}</p>
                    <p className="text-xs text-slate-500">{doc.size} • {doc.uploadedAt}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
